import React, { useState, useEffect } from 'react';
import { matchPath, withRouter, RouteComponentProps } from 'react-router-dom';
import clsx from 'clsx';
import { makeStyles, List, Drawer, IconButton, ListItem } from '@material-ui/core';
import { ChevronLeft, Menu } from '@material-ui/icons';
import NavGroup from './NavGroup';
import { tabRoutes } from '../../../shared/config/routesConfig';

const drawerWidth = 240;
const useStyles = makeStyles((theme) => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerClose: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: theme.spacing(7) + 1,
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(8) + 1,
    },
  },
  collapseBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 0.5),
  },
  sidebar: {
    height: '100%',
    background: '#ffffff',
    color: 'black',
    zIndex: 1000,
    boxShadow: '1px 0 0 0 rgba(154, 154, 154, 0.5)',
  },
  navList: {
    paddingTop: '0px',
    fontSize: '1.2em',
  },
  listItemIcon: {
    color: 'black !important',
    fontWeight: 'bold',
  },
  collapseBarIcon: {
    top: 'inherit !important',
  },
  collapseButton: {
    padding: '7px !important',
  },
  dummy: {
    // This height must always be equal to the height of the header in Dashboard CSS
    height: '80px',
  },
}));

type SidebarProps = RouteComponentProps;

const Sidebar: React.FC<SidebarProps> = ({ location }: SidebarProps) => {
  const [collapseBarState, setCollapseBarState] = useState(true);

  const [selectedIndex, setSelectedIndex] = useState<number>(-1);

  useEffect(() => {
    const routes = tabRoutes;
    let matchFound = false;
    for (let i = 0; i < routes.length; i += 1) {
      let { link, exact } = routes[i];
      if (routes[i].props && routes[i].props?.basePath) {
        link = routes[i].props?.basePath as string;
        exact = false;
      }
      if (
        matchPath(location.pathname, {
          path: link,
          exact,
        })
      ) {
        matchFound = true;
        setSelectedIndex(i);
        break;
      }
    }

    if (!matchFound) {
      setSelectedIndex(-1);
    }
  }, [location]);

  const toggleSidebar = () => {
    setCollapseBarState((sideBarState) => !sideBarState);
  };

  const classes = useStyles();

  return (
    <div>
      <Drawer
        variant="permanent"
        className={clsx(classes.drawer, {
          [classes.drawerOpen]: collapseBarState,
          [classes.drawerClose]: !collapseBarState,
        })}
        classes={{
          paper: clsx({
            [classes.drawerOpen]: collapseBarState,
            [classes.drawerClose]: !collapseBarState,
            [classes.collapseBarIcon]: true,
          }),
        }}
      >
        <div className={classes.collapseBar}>
          <IconButton onClick={toggleSidebar} className={classes.collapseButton}>
            {collapseBarState ? (
              <ChevronLeft className={classes.listItemIcon} fontSize="large" />
            ) : (
              <Menu className={classes.listItemIcon} fontSize="large" />
            )}
          </IconButton>
        </div>
        <div className={classes.sidebar}>
          <List className={classes.navList}>
            {tabRoutes.map((linkObj, index) => (
              <NavGroup key={linkObj.name} linkObj={linkObj} index={index} selectedIndex={selectedIndex} />
            ))}
            {/* Drawer always renders from the top of the window. In other words, the header is not clipped. To overcome
            this, we align the top of collapseBarIcon with the bottom of the header. However, the Drawer's height is
            always measured from the top of the header. So if the window height is less than the (drawer height + header
            height) but greater than the drawer height, no scroll bars are drawn. To fix it, we should look into Clipped
            Drawer. However for now we are solving it in a hacky way. To ensure proper scrolling, we simply render another
            blank ListItem (named dummy) with the same height as the header */}
            <ListItem className={classes.dummy} key="dummy" />
          </List>
        </div>
      </Drawer>
    </div>
  );
};

export default withRouter(Sidebar);
